import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiTrash2, FiStar } from 'react-icons/fi';
import { api, endpoints } from '../apiConfig';
import { prepareImageFiles, IMAGE_ACCEPT } from '../utils/imagePrep';
import { MAX_EXHIBITION_PHOTOS, uploadExhibitionPhotos } from '../utils/exhibitionPhotos';
import '../styles/ArtworkUpload.css';

const reorder = (list, fromIndex, toIndex) => {
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

/**
 * With an exhibitionId, changes save immediately (edit).
 * Without one, files are held until the exhibition exists (create).
 */
const ExhibitionPhotos = ({
  exhibitionId = null,
  photos = [],
  onPhotosChange,
  pendingFiles = [],
  pendingCoverIndex = 0,
  onPendingChange
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [maxBytes, setMaxBytes] = useState(10 * 1024 * 1024);
  const fileInputRef = useRef(null);
  const previews = useRef(new Map());

  const saved = Boolean(exhibitionId);
  const count = saved ? photos.length : pendingFiles.length;
  const room = MAX_EXHIBITION_PHOTOS - count;

  useEffect(() => {
    let cancelled = false;
    api.get(endpoints.artwork.uploadLimits)
      .then((response) => {
        if (!cancelled && response.data?.maxFileSizeMB) {
          setMaxBytes(response.data.maxFileSizeMB * 1024 * 1024);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => () => {
    previews.current.forEach((url) => URL.revokeObjectURL(url));
    previews.current.clear();
  }, []);

  const previewUrl = (file) => {
    if (!previews.current.has(file)) {
      previews.current.set(file, URL.createObjectURL(file));
    }
    return previews.current.get(file);
  };

  const reportRejected = (rejected) => {
    if (rejected.length === 0) return;
    if (rejected.length === 1) {
      toast.error(`"${rejected[0].name}" could not be added: ${rejected[0].reason}`, { autoClose: 6000 });
    } else {
      toast.error(`${rejected.length} photos could not be added`, { autoClose: 5000 });
    }
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (room <= 0) {
      toast.error(`An exhibition can have up to ${MAX_EXHIBITION_PHOTOS} photos. Remove one to add another.`);
      return;
    }
    if (files.length > room) {
      toast.warning(`Only ${room} more photo${room === 1 ? '' : 's'} fit. The rest were not added.`, { autoClose: 5000 });
    }

    setBusy(true);
    try {
      const { ready, rejected } = await prepareImageFiles(files.slice(0, room), { maxBytes });
      reportRejected(rejected);
      if (ready.length === 0) return;

      if (saved) {
        const next = await uploadExhibitionPhotos(exhibitionId, ready);
        onPhotosChange(next);
        toast.success(`${ready.length} photo${ready.length === 1 ? '' : 's'} added`, { autoClose: 3000 });
      } else {
        onPendingChange([...pendingFiles, ...ready], pendingFiles.length === 0 ? 0 : pendingCoverIndex);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'The photos did not upload. Please try again.', { autoClose: 6000 });
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const makeCover = async (photo) => {
    if (photo.is_cover || busy) return;
    setBusy(true);
    try {
      const response = await api.put(`/exhibitions/${exhibitionId}/photos/${photo.id}/cover`);
      onPhotosChange(response.data?.photos || photos);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not change the cover photo.');
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (photo) => {
    if (busy) return;
    if (!window.confirm('Remove this photo from the exhibition?')) return;
    setBusy(true);
    try {
      const response = await api.delete(`/exhibitions/${exhibitionId}/photos/${photo.id}`);
      onPhotosChange(response.data?.photos || photos.filter((item) => item.id !== photo.id));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove the photo.');
    } finally {
      setBusy(false);
    }
  };

  const moveSaved = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex || Number.isNaN(fromIndex) || busy) return;
    const previous = photos;
    const next = reorder(photos, fromIndex, toIndex);
    onPhotosChange(next);
    try {
      const response = await api.put(`/exhibitions/${exhibitionId}/photos/order`, {
        photo_ids: next.map((photo) => photo.id)
      });
      onPhotosChange(response.data?.photos || next);
    } catch (err) {
      onPhotosChange(previous);
      toast.error(err.response?.data?.error || 'Could not save the new order.');
    }
  };

  const removePending = (index) => {
    const next = pendingFiles.filter((_, i) => i !== index);
    let cover = pendingCoverIndex;
    if (index === pendingCoverIndex) cover = 0;
    else if (index < pendingCoverIndex) cover -= 1;
    onPendingChange(next, Math.max(0, Math.min(cover, next.length - 1)));
  };

  const movePending = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || Number.isNaN(fromIndex)) return;
    const next = reorder(pendingFiles, fromIndex, toIndex);
    const coverFile = pendingFiles[pendingCoverIndex];
    onPendingChange(next, Math.max(0, next.indexOf(coverFile)));
  };

  const tileDropHandlers = (index, move) => ({
    draggable: true,
    onDragStart: (event) => event.dataTransfer.setData('text/plain', String(index)),
    onDragOver: (event) => event.preventDefault(),
    onDrop: (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      if (event.dataTransfer.files?.length) {
        addFiles(event.dataTransfer.files);
        return;
      }
      move(parseInt(event.dataTransfer.getData('text/plain'), 10), index);
    }
  });

  const areaHandlers = room > 0 && !busy ? {
    onDragOver: (event) => {
      event.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: (event) => {
      event.preventDefault();
      setIsDragging(false);
    },
    onDrop: (event) => {
      event.preventDefault();
      setIsDragging(false);
      addFiles(event.dataTransfer.files);
    }
  } : {};

  return (
    <div className="exhibition-photos">
      {saved && photos.length > 0 && (
        <div className="existing-images">
          <h3>Exhibition photos ({photos.length}/{MAX_EXHIBITION_PHOTOS})</h3>
          <div className="images-grid">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className={`existing-image ${photo.is_cover ? 'primary' : ''}`}
                style={{ cursor: 'grab' }}
                {...tileDropHandlers(index, moveSaved)}
              >
                <img src={photo.image_url} alt={`Exhibition photo ${index + 1}`} className="preview-image" />
                <div className="existing-image-actions">
                  {photo.is_cover ? (
                    <span className="primary-badge">Cover</span>
                  ) : (
                    <button type="button" className="make-primary-button" onClick={() => makeCover(photo)} disabled={busy}>
                      Make cover
                    </button>
                  )}
                  <button type="button" className="remove-image-button" onClick={() => removePhoto(photo)} disabled={busy}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="existing-images-note">
            The Cover photo represents the exhibition on cards and at the top of its page. Drag photos to reorder them. Changes save immediately.
          </p>
        </div>
      )}

      <div
        className={`upload-area ${isDragging ? 'dragging' : ''} ${!saved && pendingFiles.length > 0 ? 'has-files' : ''} ${room <= 0 ? 'disabled' : ''}`}
        style={{ marginTop: saved && photos.length > 0 ? '1rem' : 0 }}
        onClick={room > 0 && !busy ? () => fileInputRef.current?.click() : undefined}
        {...areaHandlers}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          onChange={(event) => addFiles(event.target.files)}
          className="file-input"
          multiple
          disabled={room <= 0 || busy}
        />

        {!saved && pendingFiles.length > 0 ? (
          <div className="images-preview">
            <div className="images-grid">
              {pendingFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className={`image-preview ${index === pendingCoverIndex ? 'primary' : ''}`}
                  onClick={(event) => event.stopPropagation()}
                  {...tileDropHandlers(index, movePending)}
                >
                  <img src={previewUrl(file)} alt={`Exhibition photo ${index + 1}`} className="preview-image" />
                  <div className="image-overlay">
                    <button
                      type="button"
                      className={`primary-button ${index === pendingCoverIndex ? 'active' : ''}`}
                      onClick={() => onPendingChange(pendingFiles, index)}
                      title={index === pendingCoverIndex ? 'Cover photo' : 'Use as cover'}
                    >
                      <FiStar />
                    </button>
                    <button type="button" className="delete-button" onClick={() => removePending(index)} title="Remove photo">
                      <FiTrash2 />
                    </button>
                  </div>
                  <div className="image-info">
                    <span className="image-number">{index + 1}</span>
                    {index === pendingCoverIndex && <span className="primary-badge">Cover</span>}
                  </div>
                </div>
              ))}
            </div>
            {room > 0 && (
              <div className="upload-more">
                <FiUploadCloud size={24} />
                <span>Add more photos ({pendingFiles.length}/{MAX_EXHIBITION_PHOTOS})</span>
              </div>
            )}
          </div>
        ) : (
          <div className="upload-prompt">
            <FiUploadCloud size={40} className="upload-icon" />
            <p>
              {room <= 0
                ? `This exhibition has ${MAX_EXHIBITION_PHOTOS} photos. Remove one to add another.`
                : busy
                  ? 'Uploading…'
                  : <>Drag and drop photos here, or <span className="browse-text">browse</span></>}
            </p>
            <p className="file-info">
              Installation views, the space, opening night, details<br />
              Up to {MAX_EXHIBITION_PHOTOS} photos, {Math.round(maxBytes / (1024 * 1024))}MB each • JPEG, PNG, WebP, GIF, HEIC
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

ExhibitionPhotos.propTypes = {
  exhibitionId: PropTypes.string,
  photos: PropTypes.arrayOf(PropTypes.object),
  onPhotosChange: PropTypes.func,
  pendingFiles: PropTypes.arrayOf(PropTypes.object),
  pendingCoverIndex: PropTypes.number,
  onPendingChange: PropTypes.func
};

export default ExhibitionPhotos;
